<script setup>
// Fase 59 — cadastro de um produto/oferta do catálogo.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import PipelineProductsAPI from 'dashboard/api/pipeline/products';
import {
  formatEditableNumber,
  parseLocalizedNumber,
  resolveAccountCurrency,
} from '../utils/localeFormatting';

const props = defineProps({
  show: { type: Boolean, default: false },
  product: { type: Object, default: null },
  pipelines: { type: Array, default: () => [] },
});

const emit = defineEmits(['close', 'saved']);
const { t, locale } = useI18n();
const store = useStore();

const name = ref('');
const sku = ref('');
const description = ref('');
const category = ref('');
const defaultValue = ref('');
const restrictedPipelineIds = ref([]);
const currencyCode = ref('');
const submitting = ref(false);
// Mensagens do SERVIDOR (já em texto final) ficam aqui; a falha genérica vira
// sinalizador e é traduzida no template, senão congelaria o idioma do momento.
const errors = ref([]);
const genericFailure = ref(false);

const isEditing = computed(() => Boolean(props.product?.id));

// Escopo em dois passos. Abre em "todos" quando não há restrição — que é o
// caso da maioria dos produtos — e só revela a escolha de funis quando o
// operador pede.
const restrictScope = ref('all');
const pipelineSearch = ref('');

// O servidor trata restrição corrompida como fail-closed (indisponível em todo
// lugar) e devolve `pipeline_ids: []` + este sinalizador. A lista de seleção
// abre vazia, então SALVAR grava `[]` de verdade — ou seja, disponível em
// TODOS os funis. Precisa ficar explícito antes do clique, não depois.
const restrictionIsBroken = computed(
  () => props.product?.pipeline_ids_malformed === true
);

// Helper canônico do módulo: lê a conta do store E da sessão, normaliza o
// código e cai em BRL só no fim. A versão à mão daqui só olhava um lugar.
const accountCurrency = computed(() => resolveAccountCurrency(store));

const reset = () => {
  const source = props.product || {};
  name.value = source.name || '';
  sku.value = source.sku || '';
  description.value = source.description || '';
  category.value = source.category || '';
  // O backend devolve "2450.0"; o campo pede "0,00". Mostrar o número cru num
  // campo com placeholder brasileiro faz o operador achar que o valor está
  // errado, então reapresentamos no formato que ele digitaria.
  defaultValue.value = formatEditableNumber(source.default_value, locale.value);
  // Vazio = herda a moeda da conta; o placeholder mostra qual é.
  currencyCode.value = source.currency || '';
  restrictedPipelineIds.value = Array.isArray(source.pipeline_ids)
    ? [...source.pipeline_ids]
    : [];
  // Restrição CORROMPIDA chega como lista vazia — abrir em "todos os funis"
  // faria o Salvar gravar `[]` de verdade e transformar em global um produto
  // que o servidor tratava como indisponível em todo lugar. Abre em "escolher",
  // e o `scopeIsEmpty` segura o Salvar até alguém decidir conscientemente.
  restrictScope.value =
    restrictedPipelineIds.value.length || source.pipeline_ids_malformed
      ? 'some'
      : 'all';
  pipelineSearch.value = '';
  errors.value = [];
  genericFailure.value = false;
  submitting.value = false;
};

watch(
  () => props.show,
  visible => visible && reset(),
  { immediate: true }
);

const parsedValue = computed(() => {
  const raw = String(defaultValue.value ?? '').trim();
  if (!raw) return null;
  const localized = parseLocalizedNumber(raw, locale.value, {
    currency: accountCurrency.value,
  });
  if (localized !== null) return localized;
  const canonical = Number(raw);
  return Number.isFinite(canonical) ? canonical : null;
});

const hasValueError = computed(() => {
  const raw = String(defaultValue.value ?? '').trim();
  if (!raw) return false;
  return parsedValue.value === null || parsedValue.value < 0;
});

// Só rejeita o que foi DIGITADO errado: vazio é legítimo (herda da conta).
const normalizedCurrency = computed(() =>
  String(currencyCode.value ?? '')
    .trim()
    .toUpperCase()
);
const hasCurrencyError = computed(
  () =>
    normalizedCurrency.value !== '' &&
    !/^[A-Z]{3}$/.test(normalizedCurrency.value)
);

// "Escolher funis" com a lista VAZIA seria enviado como `[]`, que o servidor lê
// como "todos os funis" — a tela diria uma coisa e o produto valeria em outra.
// Ou escolhe pelo menos um, ou volta para "todos os funis".
const scopeIsEmpty = computed(
  () =>
    restrictScope.value === 'some' && restrictedPipelineIds.value.length === 0
);

const canSubmit = computed(
  () =>
    name.value.trim().length > 0 &&
    !hasValueError.value &&
    !hasCurrencyError.value &&
    !scopeIsEmpty.value &&
    !submitting.value
);

// Funis oferecidos: os que existem + os que a restrição aponta e sumiram. Sem
// os removidos, um produto restrito a um funil apagado fica indisponível em
// todo lugar e SEM controle para consertar — a única tela do catálogo é esta.
const selectablePipelines = computed(() => {
  const known = props.pipelines.map(pipeline => ({
    id: pipeline.id,
    name: pipeline.name,
    missing: false,
  }));
  const knownIds = new Set(known.map(pipeline => pipeline.id));
  const orphans = restrictedPipelineIds.value
    .filter(id => !knownIds.has(id))
    .map(id => ({
      id,
      name: t('PIPELINE.PRODUCTS.REMOVED_PIPELINE', { id }),
      missing: true,
    }));
  return [...known, ...orphans];
});

const selectedPipelines = computed(() =>
  selectablePipelines.value.filter(pipeline =>
    restrictedPipelineIds.value.includes(pipeline.id)
  )
);

// Só filtra pelo termo enquanto o campo de busca EXISTE. Ao selecionar funis a
// lista encolhe, o campo some abaixo de 7 opções e o termo digitado ficaria
// filtrando invisivelmente — o operador veria opções sumirem sem controle
// nenhum na tela para explicar.
const showsPipelineSearch = computed(() => selectablePipelines.value.length > 6);

const availablePipelines = computed(() => {
  const term = showsPipelineSearch.value
    ? pipelineSearch.value.trim().toLocaleLowerCase()
    : '';
  return selectablePipelines.value.filter(
    pipeline =>
      !restrictedPipelineIds.value.includes(pipeline.id) &&
      (!term || pipeline.name.toLocaleLowerCase().includes(term))
  );
});

// Voltar para "todos os funis" LIMPA a restrição — senão o rótulo diria uma
// coisa e o payload enviaria outra.
const setScope = scope => {
  restrictScope.value = scope;
  if (scope === 'all') restrictedPipelineIds.value = [];
};

const togglePipeline = id => {
  const index = restrictedPipelineIds.value.indexOf(id);
  if (index >= 0) restrictedPipelineIds.value.splice(index, 1);
  else restrictedPipelineIds.value.push(id);
};

const submit = async () => {
  if (!canSubmit.value) return;
  submitting.value = true;
  errors.value = [];
  genericFailure.value = false;

  const payload = {
    name: name.value.trim(),
    sku: sku.value.trim() || null,
    description: description.value.trim() || null,
    category: category.value.trim() || null,
    default_value: parsedValue.value,
    // O que o operador digitou; vazio herda a moeda do produto (na edição) ou
    // a da conta. Reenviar sempre a moeda da conta reescrevia em silêncio um
    // produto cadastrado em outra — mesmo número, significado diferente.
    currency:
      normalizedCurrency.value ||
      props.product?.currency ||
      accountCurrency.value,
    pipeline_ids: restrictedPipelineIds.value,
  };

  try {
    const { data } = isEditing.value
      ? await PipelineProductsAPI.updateProduct(props.product.id, payload)
      : await PipelineProductsAPI.createProduct(payload);
    emit('saved', data?.product);
    emit('close');
  } catch (error) {
    // Mostra a mensagem do servidor (SKU duplicado, funil de outra conta) em
    // vez de um genérico: o operador precisa saber o que corrigir.
    const serverErrors = error?.response?.data?.errors;
    if (Array.isArray(serverErrors) && serverErrors.length) {
      errors.value = serverErrors;
    } else {
      genericFailure.value = true;
    }
  } finally {
    submitting.value = false;
  }
};
</script>

<template>
  <Modal :show="show" @close="emit('close')">
    <section
      class="flex max-h-[min(90vh,48rem)] max-w-lg flex-col overflow-hidden"
    >
      <header class="px-4 pt-5 sm:px-6 sm:pt-6">
        <h2 class="mb-1 pr-10 text-heading-2 text-n-slate-12">
          {{
            isEditing
              ? t('PIPELINE.PRODUCTS.EDIT_TITLE')
              : t('PIPELINE.PRODUCTS.NEW_TITLE')
          }}
        </h2>
        <p class="text-body-main text-n-slate-11">
          {{ t('PIPELINE.PRODUCTS.FORM_HINT') }}
        </p>
      </header>

      <!-- O erro fica FORA da área rolável, logo abaixo do cabeçalho: dentro
           dela ele nascia embaixo da lista de funis, fora da dobra, e o
           usuário via só o Salvar não fazer nada. -->
      <div
        v-if="errors.length || genericFailure"
        class="mx-4 mt-4 rounded-lg bg-n-ruby-3 p-3 text-sm text-n-ruby-11 sm:mx-6"
        role="alert"
      >
        <ul v-if="errors.length">
          <li v-for="message in errors" :key="message">{{ message }}</li>
        </ul>
        <p v-else>{{ t('PIPELINE.PRODUCTS.SAVE_ERROR') }}</p>
      </div>

      <div
        v-if="restrictionIsBroken"
        class="mx-4 mt-4 rounded-lg bg-n-amber-3 p-3 text-sm text-n-amber-11 sm:mx-6"
        role="status"
      >
        {{ t('PIPELINE.PRODUCTS.RESTRICTION_BROKEN_HINT') }}
      </div>

      <div class="flex flex-col gap-4 overflow-y-auto px-4 py-5 sm:px-6">
        <!-- Identidade na primeira linha; preço na segunda. Duas colunas
             encurtam o formulário o suficiente para o Salvar e as mensagens de
             erro caberem sem rolagem. -->
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            v-model="name"
            :label="t('PIPELINE.PRODUCTS.NAME')"
            :placeholder="t('PIPELINE.PRODUCTS.NAME_PLACEHOLDER')"
            :disabled="submitting"
          />
          <Input
            v-model="category"
            :label="t('PIPELINE.PRODUCTS.CATEGORY')"
            :placeholder="t('PIPELINE.PRODUCTS.CATEGORY_PLACEHOLDER')"
            :disabled="submitting"
          />
        </div>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            v-model="sku"
            :label="t('PIPELINE.PRODUCTS.SKU')"
            :placeholder="t('PIPELINE.PRODUCTS.SKU_PLACEHOLDER')"
            :disabled="submitting"
            :message="t('PIPELINE.PRODUCTS.SKU_HINT')"
            message-type="info"
          />
          <div class="grid grid-cols-[1fr_6rem] gap-3">
            <Input
              v-model="defaultValue"
              type="text"
              inputmode="decimal"
              :label="t('PIPELINE.PRODUCTS.DEFAULT_VALUE')"
              :placeholder="t('PIPELINE.PRODUCTS.DEFAULT_VALUE_PLACEHOLDER')"
              :disabled="submitting"
              :message="hasValueError ? t('PIPELINE.ERRORS.VALUE_INVALID') : ''"
              :message-type="hasValueError ? 'error' : 'info'"
            />
            <!-- A API e a listagem já suportam produto em moeda diferente da
                 conta; sem este campo, a única tela do catálogo não conseguia
                 cadastrar um. -->
            <Input
              v-model="currencyCode"
              type="text"
              maxlength="3"
              :label="t('PIPELINE.PRODUCTS.CURRENCY')"
              :placeholder="accountCurrency"
              :disabled="submitting"
              :message="
                hasCurrencyError ? t('PIPELINE.PRODUCTS.CURRENCY_INVALID') : ''
              "
              :message-type="hasCurrencyError ? 'error' : 'info'"
            />
          </div>
        </div>

        <TextArea
          v-model="description"
          :label="t('PIPELINE.PRODUCTS.DESCRIPTION')"
          :disabled="submitting"
          auto-height
          min-height="4rem"
        />

        <!-- Escolha do escopo em DOIS passos: a maioria dos produtos vale em
             todos os funis, e a lista de checkboxes obrigava todo mundo a rolar
             por ela — com 12 funis o botão Salvar saía da dobra e a mensagem de
             erro do servidor ficava invisível. -->
        <!-- Continua visível enquanto o escopo for "escolher": removendo o
             último funil (ou o órfão de um funil apagado, numa conta sem outros
             funis) a lista fica vazia, e sumir com o bloco levaria junto o
             botão de voltar para "todos" E o aviso de por que o Salvar está
             bloqueado — beco sem saída. -->
        <fieldset
          v-if="selectablePipelines.length || restrictScope === 'some'"
          class="rounded-lg border border-n-weak bg-n-solid-2 p-3"
        >
          <legend class="px-1 text-label font-medium text-n-slate-12">
            {{ t('PIPELINE.PRODUCTS.RESTRICT_TO_PIPELINES') }}
          </legend>

          <div class="mb-2 flex gap-1 rounded-lg bg-n-alpha-1 p-1">
            <button
              v-for="scope in [
                { key: 'all', label: t('PIPELINE.PRODUCTS.SCOPE_ALL') },
                { key: 'some', label: t('PIPELINE.PRODUCTS.SCOPE_SOME') },
              ]"
              :key="scope.key"
              type="button"
              class="flex-1 rounded-md px-3 py-1.5 text-label font-medium transition-colors"
              :class="
                restrictScope === scope.key
                  ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-11 hover:text-n-slate-12'
              "
              :aria-pressed="restrictScope === scope.key"
              :disabled="submitting"
              @click="setScope(scope.key)"
            >
              {{ scope.label }}
            </button>
          </div>

          <template v-if="restrictScope === 'some'">
            <div class="flex flex-wrap gap-1.5">
              <button
                v-for="pipeline in selectedPipelines"
                :key="pipeline.id"
                type="button"
                class="flex items-center gap-1.5 rounded-full py-1 pl-3 pr-2 text-label-small font-medium transition-colors"
                :class="
                  pipeline.missing
                    ? 'bg-n-ruby-3 text-n-ruby-11'
                    : 'bg-n-brand/10 text-n-brand'
                "
                :disabled="submitting"
                :aria-label="
                  t('PIPELINE.PRODUCTS.REMOVE_PIPELINE', {
                    name: pipeline.name,
                  })
                "
                @click="togglePipeline(pipeline.id)"
              >
                {{ pipeline.name }}
                <Icon icon="i-lucide-x" class="size-3" />
              </button>
            </div>

            <!-- Busca só quando há muitos: com 3 funis ela seria um obstáculo
                 a mais entre o operador e a escolha. -->
            <Input
              v-if="showsPipelineSearch"
              v-model="pipelineSearch"
              type="search"
              class="mt-2"
              :placeholder="t('PIPELINE.PRODUCTS.SEARCH_PIPELINE')"
              :disabled="submitting"
            />

            <div class="mt-2 flex max-h-32 flex-wrap gap-1.5 overflow-y-auto">
              <button
                v-for="pipeline in availablePipelines"
                :key="pipeline.id"
                type="button"
                class="rounded-full border border-n-weak px-3 py-1 text-label-small text-n-slate-11 transition-colors hover:border-n-brand hover:text-n-slate-12"
                :disabled="submitting"
                @click="togglePipeline(pipeline.id)"
              >
                + {{ pipeline.name }}
              </button>
              <p
                v-if="!availablePipelines.length"
                class="text-label-small text-n-slate-10"
              >
                {{
                  showsPipelineSearch && pipelineSearch.trim()
                    ? t('PIPELINE.PRODUCTS.NO_PIPELINE_FOUND')
                    : selectablePipelines.length
                      ? t('PIPELINE.PRODUCTS.NO_PIPELINE_LEFT')
                      : t('PIPELINE.PRODUCTS.NO_PIPELINE_AT_ALL')
                }}
              </p>
            </div>

            <p
              v-if="scopeIsEmpty"
              class="mt-2 text-label-small text-n-ruby-11"
              role="alert"
            >
              {{ t('PIPELINE.PRODUCTS.SCOPE_EMPTY') }}
            </p>
          </template>

          <p v-else class="text-label-small text-n-slate-10">
            {{ t('PIPELINE.PRODUCTS.RESTRICT_HINT') }}
          </p>
        </fieldset>
      </div>

      <footer
        class="flex flex-col-reverse gap-2 border-t border-n-weak px-4 py-4 sm:flex-row sm:px-6"
      >
        <Button
          variant="outline"
          color="slate"
          size="lg"
          class="w-full flex-1"
          :label="t('PIPELINE.PRODUCTS.CANCEL')"
          :disabled="submitting"
          @click="emit('close')"
        />
        <Button
          color="blue"
          size="lg"
          class="w-full flex-1"
          :label="t('PIPELINE.PRODUCTS.SAVE')"
          :is-loading="submitting"
          :disabled="!canSubmit"
          @click="submit"
        />
      </footer>
    </section>
  </Modal>
</template>
